import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MBTI_GROUPS, MbtiBadge, mbtiInfo, clubInfo } from "@/lib/mbti";
import { Briefcase, Save, ExternalLink, BookOpen, Trophy } from "lucide-react";
import PushToggle from "@/components/PushToggle";
import InstallAppButton from "@/components/InstallAppButton";
import IdealPessoalPanel from "@/components/IdealPessoalPanel";
import { MissionViaPanel, ValuePropositionPanel } from "@/pages/MyProfile";

const MBTI_CODES = MBTI_GROUPS.flatMap((g) => (g.types || []).map((t) => ({ ...t, club: g.club })));

/**
 * Staff version of "O Meu Perfil". Mirrors CoachProfile but without CoachChampions.
 * Highlights department + auto-assigns club from MBTI so the user accesses the right training.
 */
export default function StaffProfile() {
  const { user, refresh } = useAuth();
  const [mbti, setMbti] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setMbti(user?.coach_mbti || ""); }, [user?.coach_mbti]);

  if (!user) return <div className="kw-overline">A carregar…</div>;

  const hasMbti = !!user.coach_mbti;
  const mbtiCard = MBTI_CODES.find((m) => m.code === user.coach_mbti);

  const saveMbti = async () => {
    if (!mbti) { toast.error("Escolhe um MBTI."); return; }
    setSaving(true);
    try {
      await api.put("/my-profile/onboarding", { mbti });
      toast.success(`MBTI definido: ${mbti}. Ganhas acesso às formações do clube respetivo.`);
      refresh && await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="space-y-8 kw-fade-up" data-testid="staff-profile-page">
      <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
        <div className="w-32 h-32 md:w-36 md:h-36 rounded-full bg-[#1A1A1A] flex items-center justify-center font-display text-4xl font-black text-[#E50000] border-4 border-[#E50000]">
          {(user.name || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="kw-overline mb-2 flex items-center gap-2"><Briefcase className="w-3 h-3" /> Staff KW Lead</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">{user.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {user.department && (
              <span className="text-[11px] uppercase tracking-widest font-bold px-3 py-1 border border-[#EAB308] text-[#EAB308]">
                {user.department}
              </span>
            )}
            {user.club && <MbtiBadge club={user.club} />}
            {hasMbti && (() => {
              const info = mbtiInfo(user.coach_mbti) || clubInfo(user.club);
              return (
                <span
                  className="text-[11px] uppercase tracking-widest font-bold px-3 py-1 border inline-flex items-center gap-1"
                  style={{ color: info?.color, borderColor: info?.border, background: info?.bg }}>
                  <span className="font-mono">{user.coach_mbti}</span>
                  {user.coach_personality_label && <span>· {user.coach_personality_label}</span>}
                </span>
              );
            })()}
          </div>
          <div className="text-xs text-[#737373] mt-2">{user.email}</div>
        </div>
      </div>

      {!hasMbti ? (
        <div className="kw-card p-5 border-[#E50000]/40 bg-[#E50000]/5">
          <div className="kw-overline mb-2 text-[#E50000]">⚠ MBTI por definir</div>
          <p className="text-sm text-[#A3A3A3] mb-3">
            Como membro do staff, o teu MBTI determina o clube cujas formações vais poder consultar.
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <select className="kw-input !py-2 !w-56" value={mbti}
              onChange={(e) => setMbti(e.target.value)} data-testid="staff-mbti-select">
              <option value="">— escolher MBTI —</option>
              {MBTI_CODES.map((m) => (
                <option key={m.code} value={m.code}>{m.code} · {m.label} ({m.club})</option>
              ))}
            </select>
            <button onClick={saveMbti} disabled={saving || !mbti}
              className="kw-btn-primary !py-2 inline-flex items-center gap-2" data-testid="staff-mbti-save">
              <Save className="w-4 h-4" /> {saving ? "A guardar…" : "Confirmar"}
            </button>
            <a href="https://www.16personalities.com/" target="_blank" rel="noopener noreferrer"
              className="text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 ml-auto">
              <ExternalLink className="w-3 h-3" /> Fazer teste (12 min)
            </a>
          </div>
        </div>
      ) : (
        <div className="kw-card p-5">
          <div className="kw-overline mb-2">Personalidade · {user.coach_mbti}</div>
          {mbtiCard && (
            <div className="text-sm text-[#A3A3A3]">
              <strong className="text-white">{mbtiCard.label}</strong> · família <strong className="text-white">{mbtiCard.club}</strong>.
              Para alterar, fala com o administrador.
            </div>
          )}
        </div>
      )}

      <div className="kw-card p-5">
        <div className="kw-overline mb-3">Acesso rápido</div>
        <div className="flex flex-wrap gap-2">
          <a href="/training" className="kw-btn-secondary text-xs inline-flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> Formações do meu clube
          </a>
          <a href="/rankings" className="kw-btn-secondary text-xs inline-flex items-center gap-2">
            <Trophy className="w-4 h-4" /> Rankings
          </a>
        </div>
      </div>

      {hasMbti && (
        <>
          {/* Personal Profile — Mission, Vision, Principles, VIA top-5 */}
          <MissionViaPanel
            player={user}
            endpoint="/my-profile-user"
            viaImportEndpoint="/my-profile-user/via-import"
            onSaved={refresh}
          />
          {/* Value Proposition — Knowledge, Process, Relationship */}
          <ValuePropositionPanel
            player={user}
            endpoint="/my-profile-user"
            onSaved={refresh}
          />
        </>
      )}

      <PushToggle />
      <InstallAppButton />
      <IdealPessoalPanel />
    </div>
  );
}
